import geoPath from 'd3-geo/src/path'
import { select } from 'd3-selection'
import { layerGroupList, layerIds, layerList } from '@/assets/js/model/layer'
import { styleAttributeIds } from '@/assets/js/model/style-attributes'
import {
  findLayerStyle,
  getDrawMethod,
  getWatermarkFontSize,
  WATERMARK_TEXT,
} from '@/assets/js/util/rendering-adapter/util'

const SvgSerializer = new window.XMLSerializer()

const attributes = {
  [styleAttributeIds.FILL_COLOR]: 'fill',
  [styleAttributeIds.LINE_WIDTH]: 'stroke-width',
  [styleAttributeIds.LINE_COLOR]: 'stroke',
  [styleAttributeIds.LINE_DASH]: 'stroke-dasharray',
}

/**
 * A list of draw methods
 * @type {any}
 */
const drawMethodList = [
  {
    id: null,
    draw({ applyProjection, element, layer, theme }) {
      const { styles } = findLayerStyle(theme, layer)
      const pathElement = drawPath({ applyProjection, element, layer, styles })
      stylePath({ layer, pathElement, styles })
    },
  },
  {
    id: layerIds.BACKGROUND,
    draw({ element, layer, theme, width, height }) {
      const { styles } = findLayerStyle(theme, layer)
      const pathElement = getPath(element, layer, 'rect')
      pathElement.attr('x', 0).attr('y', 0).attr('width', width).attr('height', height)
      stylePath({ layer, pathElement, styles })
    },
  },
]

/**
 * Return a path from the element, if none found, append it
 * @param {SVGSVGElement} element
 * @param {any} layer
 * @param {string} tag
 * @return {any}
 */
function getPath(element, layer, tag = 'path') {
  const { layerIndex, pathId, layerGroupId } = layer.config
  const path = element.querySelector(`#${pathId}`)
  const { groupId } = layerGroupList.find(({ id }) => id === layerGroupId)

  if (path) {
    return select(path)
  }

  return select(element)
    .select(`#${groupId}`)
    .append(tag)
    .attr('id', pathId)
    .attr('layer-index', layerIndex)
}

/**
 * Implement styles for a given path
 * @param {any} layer
 * @param {any} pathElement
 * @param {any} styles
 */
function stylePath({ layer, pathElement, styles }) {
  if (!layer.config.styleLayer) {
    return
  }

  Object.entries(styles).forEach(([key, value]) => {
    if (attributes[key]) {
      pathElement.attr(attributes[key], value)
    }
  })

  if (!styles[styleAttributeIds.FILL_COLOR]) {
    pathElement.attr(attributes[styleAttributeIds.FILL_COLOR], 'none')
  }

  if (!styles[styleAttributeIds.LINE_COLOR]) {
    pathElement.attr(attributes[styleAttributeIds.LINE_COLOR], 'none')
  }
}

/**
 * Remove all layers that are not in the loaded layers list
 * @param {SVGSVGElement} element
 * @param {any[]} layers
 */
function removeDisabledLayers({ element, layers }) {
  const loadedLayerPathIds = layers.map(({ config }) => config.pathId)

  layerGroupList.forEach(({ groupId }) => {
    const root = element.querySelector(`#${groupId}`)
    const children = [...root.children]

    children
      .map((path) => path.getAttribute('id'))
      .filter((pathId) => !loadedLayerPathIds.includes(pathId))
      .forEach((targetPathId) => {
        const targetLayer = layerList.find(({ pathId }) => pathId === targetPathId)

        if (targetLayer) {
          select(`#${targetPathId}`).remove()
        }
      })
  })
}

/**
 * Implement the map clipping
 * @param {any} applyProjection
 * @param {SVGSVGElement} element
 * @param {any[]} layers
 */
function implementClipping({ applyProjection, element, layers }) {
  const { config, data } = layers.find(({ id }) => id === layerIds.CLIP_PATH)
  const elementSelection = select(element)

  if (!element.querySelector('defs')) {
    elementSelection.append('defs').append('clipPath').attr('id', config.pathId).append('path')
  }

  layerGroupList.forEach(({ groupId, hasClipPath }) => {
    if (!element.querySelector(`g#${groupId}`)) {
      elementSelection
        .append('g')
        .attr('id', groupId)
        .attr('clip-path', hasClipPath ? `url(#${config.pathId})` : null)
    }
  })

  elementSelection.select(`clipPath#${config.pathId} path`).attr('d', applyProjection(data))
}

/**
 * Processor executed prior to draw method
 * @param {any} applyProjection
 * @param {SVGSVGElement} element
 * @param {any[]} layers
 * @param {string} theme
 * @param {number} width
 * @param {number} height
 */
function executeDraw({ applyProjection, element, layers, theme, width, height }) {
  layers
    .filter(({ config }) => config.styleLayer)
    .forEach((layer) => {
      const { draw } = getDrawMethod(drawMethodList, layer.id)

      draw({ applyProjection, element, layer, theme, width, height })
    })
}

/**
 * Sort a list of children by layer index
 * @param {SVGSVGElement} element
 */
function sortLayers({ element }) {
  layerGroupList.forEach(({ groupId }) => {
    const root = element.querySelector(`#${groupId}`)
    const children = [...root.children]

    children
      .sort((a, b) => Number(a.getAttribute('layer-index')) - Number(b.getAttribute('layer-index')))
      .forEach((node) => root.appendChild(node))
  })
}

/**
 * Return a data string from the given element
 * @param {SVGSVGElement} element
 * @return {string}
 */
export function createDataURL(element) {
  let source = SvgSerializer.serializeToString(element)

  if (!source.match(/^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/)) {
    source = source.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"')
  }
  if (!source.match(/^<svg[^>]+"http:\/\/www\.w3\.org\/1999\/xlink"/)) {
    source = source.replace(/^<svg/, '<svg xmlns:xlink="http://www.w3.org/1999/xlink"')
  }

  source = `<?xml version="1.0" standalone="no"?>\r\n${source}`

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(source)}`
}

/**
 * Main draw action
 * @param {SVGSVGElement} element
 * @param {any[]} layers
 * @param {any} projection
 * @param {string} theme
 * @param {number} width
 * @param {number} height
 * @param {boolean} hasWatermark
 */
export function draw({ element, layers, projection, theme, width, height, hasWatermark }) {
  const applyProjection = geoPath(projection, null)

  implementClipping({ applyProjection, element, layers })
  removeDisabledLayers({ element, layers })
  executeDraw({ applyProjection, element, layers, theme, width, height })
  sortLayers({ element })

  if (hasWatermark) {
    drawWatermark({ element, width })
  }
}

/**
 * Draw a path
 * @param {any} applyProjection
 * @param {any} data
 * @param {SVGSVGElement} element
 * @param {any} layer
 * @param {any} path
 * @return {*}
 */
export function drawPath({ applyProjection, element, layer, path }) {
  if (path) {
    return path
  }

  return getPath(element, layer)
    .attr('d', applyProjection(layer.data))
    .attr('layer-index', layer.layerIndex)
}

/**
 * Draw the watermark
 * @param {SVGSVGElement} element
 * @param {number} width
 */
export function drawWatermark({ element, width }) {
  const config = layerList.find(({ id }) => id === layerIds.WATERMARK)
  const fontSize = getWatermarkFontSize(width)

  if (!fontSize) {
    return
  }

  getPath(element, { config }, 'text')
    .text(WATERMARK_TEXT)
    .attr('x', 8)
    .attr('y', 8)
    .attr('dy', `${4 + fontSize / 2}px`)
    .attr('text-anchor', 'top')
    .style('font', `${fontSize}px Barlow, sans-serif`)
    .style('fill', '#ffffff')
    .style(
      'text-shadow',
      '-1px 0px 0 #000000, 1px 0px 0 #000000, 0px -1px 0 #000000, 0px 1px 0 #000000',
    )
}

/**
 * @param {any} layer - The geojson data containing stations
 * @param {Function} projection - The map projection
 * @param {SVGSVGElement} svg - The svg element
 * @return any[] A list of DOMPoints
 */
export function convertStations(layer, projection, svg) {
  return layer.features.map((feature) => {
    const [x, y] = projection(feature.geometry.coordinates)
    const point = svg.createSVGPoint()
    point.x = x
    point.y = y

    return point.matrixTransform(svg.getScreenCTM())
  })
}
